import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApiStore } from '@/stores/api'

import { hotReloadStore } from '@/utils/build'

import type { ComputedRef } from 'vue'

export interface EnvironmentStore {
  /**
   * Loads the environment details, including API keys.
   * Should be called before accessing any keys.
   * Keys are cached so multiple calls will not refetch if already loaded.
   */
  loadEnvironment: () => Promise<void>
  /**
   * Google Maps API key.
   */
  googleMapsKey: ComputedRef<string | null>
  /**
   * Flagsmith client details.
   */
  flagsmithDetails: ComputedRef<{ url: string, key: string } | null>
}

/**
 * Manages environment-specific settings such as API keys.
 * Keys are fetched when read for the first time and cached.
 */
export const useEnvironmentStore = defineStore('environment', (): EnvironmentStore => {
  const apiStore = useApiStore()

  const googleMapsKeyInternal = ref<string | null>(null)
  const flagsmithDetailsInternal = ref<{ url: string, key: string } | null>(null)

  let environmentLoadPromise: Promise<void> | null = null
  async function fetchEnvironmentDetails (): Promise<void> {
    try {
      const response = await apiStore.environment.details()
      googleMapsKeyInternal.value = response.googleMapsKey || null
      flagsmithDetailsInternal.value = (response.flagsmithKey && response.flagsmithUrl)
        ? { url: response.flagsmithUrl, key: response.flagsmithKey }
        : null
    } catch (error) {
      console.error('Failed to load environment details:', error)
    } finally {
      environmentLoadPromise = null
    }
  }
  const loadEnvironment: EnvironmentStore['loadEnvironment'] = async () => {
    if (googleMapsKeyInternal.value && flagsmithDetailsInternal.value) return
    if (environmentLoadPromise) return environmentLoadPromise
    environmentLoadPromise = fetchEnvironmentDetails()
    return await environmentLoadPromise
  }

  const googleMapsKey: EnvironmentStore['googleMapsKey'] = computed(() => googleMapsKeyInternal.value)
  const flagsmithDetails: EnvironmentStore['flagsmithDetails'] = computed(() => flagsmithDetailsInternal.value)

  return {
    loadEnvironment,
    googleMapsKey,
    flagsmithDetails,
  }
})

hotReloadStore(useEnvironmentStore)
